import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../framework/services/auth.service';
import { LoggingService } from '../../../../framework/logging/logging.service';
import { ProfileDataService } from '../../../services/profile-data.service';
import { SeverityLevel } from '../../../../framework/logging/severity-level.model';
import { lastValueFrom } from 'rxjs';

@Component({
    selector: 'signin-callback',
    template: `
                    <div class="container-fluid">
                        <h2 class="logon-status-info">Logging In...</h2>
                    </div>

            `
})

export class SignInRedirectCallbackComponent implements OnInit {

    constructor(
        private readonly _authService: AuthService,
        private readonly _router: Router,
        private readonly loggingService: LoggingService,
        private readonly profileDataService: ProfileDataService,
    ) { }

    ngOnInit() {
        // console.log('🔐 SignInRedirectCallbackComponent.ngOnInit() - Starting login completion...');
        // console.log('Current URL:', window.location.href);

        this._authService.completeLogin().then(async user => {
            // console.log('✅ completeLogin() promise resolved');

            // More defensive checking - ensure user exists and has required properties
            if (user?.access_token && user?.profile) {
                // console.log('✅ User has access token and profile, proceeding with login...');
                // localStorage.setItem('tasksApiToken', 'Bearer ' + user.access_token);

                try {
                    //  console.log('📋 Loading user profile...');
                    const profile = await lastValueFrom(this.profileDataService.getUserProfile(user.profile['sub']));
                    localStorage.setItem('loggedInUser', profile[1]['firstName'] + ' ' + profile[1]['lastName']);
                    localStorage.setItem('loggedInUserId', profile[1]['id']);
                    user.profile = profile[1]; // Ensure user.profile.id is set for consistency

                    // console.log('👤 User profile loaded:', profile[1]['firstName'] + ' ' + profile[1]['lastName']);

                    if (profile[1]?.teams != null && profile[1].teams.length > 0) {
                        localStorage.setItem("userTeams", JSON.stringify(profile[1].teams.map(t => t.name || 'Unknown')));
                    }
                    else if (profile[1]?.groups != null && profile[1].groups.length > 0) {
                        const teamGroups = profile[1].groups.filter(g => g.groupType === 'Team');
                        localStorage.setItem("userTeams", JSON.stringify(teamGroups.map(g => g.name || 'Unknown')));
                    }
                    else {
                        localStorage.setItem("userTeams", JSON.stringify([]));
                    }

                    // console.log('⚙️ Loading user preferences...');
                    const prefs = await lastValueFrom(this.profileDataService.getUserPreferences(user.profile['id']));
                    localStorage.setItem("userPreferences", prefs[1] ? JSON.stringify(prefs[1]) : JSON.stringify({}));
                }
                catch (error) {
                    console.error('⚠️ Error loading user profile/preferences:', error);
                    this.loggingService.logError('Error loading user profile/preferences, but continuing with login', SeverityLevel.Warning);
                    this.loggingService.logError(error, SeverityLevel.Error);
                    // Don't fail the entire login process if profile loading fails
                }

                const redirectUrl = localStorage.getItem("fromExternalUrl")

                if (redirectUrl != null) {
                    // console.log('🔀 Redirecting to external URL:', redirectUrl);
                    localStorage.removeItem("fromExternalUrl");

                    // Decode the URL and split it into path and query parameters
                    const decodedUrl = decodeURIComponent(redirectUrl);
                    const [path, queryString] = decodedUrl.split('?');
                    const queryParams: { [key: string]: string } = {};
                    if (queryString) {
                        const params = new URLSearchParams(queryString);
                        params.forEach((value, key) => {
                            queryParams[key] = value;
                        });
                    }
                    this._router.navigate([path], { queryParams });
                }
                else {
                    // console.log('🔀 Redirecting to tasks page...');
                    this._router.navigate(["tasks"]);
                }

            }
            else {
                console.error('Login completion issue: User data incomplete');
                // console.log('User object received:', {
                //   hasUser: !!user,
                //   hasAccessToken: !!user?.access_token,
                //   hasProfile: !!user?.profile,
                //   hasName: !!user?.profile?.['name']
                // });

                // Instead of immediately logging out, try to redirect to login
                // This handles cases where the login process didn't complete properly
                this.loggingService.logError('Login completion failed - redirecting to login page', SeverityLevel.Warning);
                this._router.navigate(['/login']);
            }

        }).catch(error => {
            console.error('Error during login completion:', error);
            this.loggingService.logError('Login completion error', SeverityLevel.Error);
            this.loggingService.logError(error, SeverityLevel.Error);

            // Redirect to login instead of logout to avoid getting stuck in a loop
            this._router.navigate(['/login']);
        });
    }
}